import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const CartDrawer = ({ isOpen, onClose, cart, updateQuantity, removeItem }) => {
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleCheckout = () => {
    setIsCheckingOut(true);
    // Simulate network request
    setTimeout(() => {
      setIsCheckingOut(false);
      setCheckoutSuccess(true);
      setTimeout(() => {
        setCheckoutSuccess(false);
        onClose();
      }, 3000);
    }, 1500);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            aria-hidden="true"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[400px] bg-moon-white shadow-2xl z-[70] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Shopping Cart"
          >
            <div className="flex items-center justify-between p-6 border-b border-tea-stone">
              <h2 className="font-serif text-2xl text-plum-dark">Your Cart</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-plum-dark transition-colors rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade"
                aria-label="Close cart"
              >
                <SafeIcon icon={FiIcons.FiX} className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {checkoutSuccess ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-16 h-16 bg-jade/10 text-jade rounded-full flex items-center justify-center mb-4">
                    <SafeIcon icon={FiIcons.FiCheck} className="w-8 h-8" />
                  </div>
                  <h3 className="font-serif text-2xl text-plum-dark">Order Placed</h3>
                  <p className="text-gray-600 text-sm">Thank you for your purchase. Your tea ritual begins soon.</p>
                </div>
              ) : cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-16 h-16 bg-tea-stone/50 rounded-full flex items-center justify-center mb-4 text-gray-400">
                    <SafeIcon icon={FiIcons.FiShoppingBag} className="w-8 h-8" />
                  </div>
                  <h3 className="font-serif text-xl text-plum-dark">Your cart is empty</h3>
                  <p className="text-gray-500 text-sm">Discover our curated collection of teas and teaware.</p>
                  <button
                    onClick={onClose}
                    className="mt-4 text-jade hover:text-jade-dark text-sm tracking-widest uppercase font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade focus-visible:ring-offset-2 rounded"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <ul className="space-y-6">
                  {cart.map((item) => (
                    <li key={item.id} className="flex gap-4">
                      <div className="w-20 h-20 bg-tea-stone rounded-sm overflow-hidden flex-shrink-0">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 flex flex-col justify-between">
                        <div>
                          <h4 className="font-serif text-gray-900 text-sm mb-1">{item.name}</h4>
                          <p className="text-jade-dark text-sm font-medium">${item.price}</p>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center border border-tea-stone rounded-sm">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="px-2 py-1 text-gray-500 hover:text-plum-dark transition-colors focus-visible:outline-none focus-visible:bg-tea-stone"
                              aria-label={`Decrease quantity of ${item.name}`}
                            >
                              <SafeIcon icon={FiIcons.FiMinus} className="w-3 h-3" />
                            </button>
                            <span className="text-xs font-medium w-6 text-center" aria-live="polite">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="px-2 py-1 text-gray-500 hover:text-plum-dark transition-colors focus-visible:outline-none focus-visible:bg-tea-stone"
                              aria-label={`Increase quantity of ${item.name}`}
                            >
                              <SafeIcon icon={FiIcons.FiPlus} className="w-3 h-3" />
                            </button>
                          </div>
                          <button
                            onClick={() => removeItem(item.id)}
                            className="text-xs text-gray-400 hover:text-red-500 transition-colors underline focus-visible:outline-none focus-visible:text-red-500"
                            aria-label={`Remove ${item.name} from cart`}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {!checkoutSuccess && cart.length > 0 && (
              <div className="p-6 border-t border-tea-stone bg-white/50">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-gray-600 font-medium">Subtotal</span>
                  <span className="font-serif text-xl text-plum-dark">${total.toFixed(2)}</span>
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className="w-full bg-plum-dark text-moon-white py-4 uppercase text-xs font-bold tracking-[0.2em] hover:bg-jade transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade focus-visible:ring-offset-2"
                >
                  {isCheckingOut ? (
                    <SafeIcon icon={FiIcons.FiLoader} className="w-4 h-4 animate-spin" />
                  ) : (
                    'Proceed to Checkout'
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;